import React from 'react';
import {useCart, useMoney, useLocalization, CartCost} from '@shopify/hydrogen';
import * as R from 'ramda';
import md5 from 'md5';
import type {
  Product,
  Maybe,
  ProductVariant,
  MoneyV2,
  SelectedOption,
  Image,
  CurrencyCode as CurrencyCodeType,
} from '@shopify/hydrogen/storefront-api-types';
import clsx from 'clsx';
import {LoadingSpinner} from '../collection/CollectionListingMain.client';
import {withCookies} from 'react-cookie';
import type {Cookies as CookiesType} from 'react-cookie';

//context
import {GlobalContext} from '../../GlobalProvider.client';

//TODO: check discount, now discount can be applied to other products as well discountlive.json and discountdev.json

type CartMerchandise = {
  __typename?: 'ProductVariant';
} & Pick<
  ProductVariant,
  'id' | 'availableForSale' | 'requiresShipping' | 'title' | 'sku'
> & {
    compareAtPriceV2?: Maybe<
      {
        __typename?: 'MoneyV2';
      } & Pick<MoneyV2, 'currencyCode' | 'amount'>
    >;
    priceV2: {
      __typename?: 'MoneyV2';
    } & Pick<MoneyV2, 'currencyCode' | 'amount'>;
    image?: Maybe<
      {
        __typename?: 'Image';
      } & Pick<Image, 'id' | 'url' | 'altText' | 'width' | 'height'>
    >;
    product: {
      __typename?: 'Product';
    } & Pick<Product, 'handle' | 'title'>;
    selectedOptions: Array<
      {
        __typename?: 'SelectedOption';
      } & Pick<SelectedOption, 'name' | 'value'>
    >;
  };

function CartSummaryAndDiscounts({
  isLoading,
  setIsLoading,
  cookies,
}: {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  cookies: CookiesType;
}) {
  const {lines, totalQuantity, id, cost} = useCart();
  const {
    language: {isoCode},
    country: {isoCode: countryCode},
  } = useLocalization();
  const languageCode =
    isoCode === 'ZH_TW' ? 'zh-TW' : isoCode === 'ZH_CN' ? 'zh-CN' : 'en';
  const currency =
    (cookies.get('hk-currency') as CurrencyCodeType) ??
    ('HKD' as CurrencyCodeType);
  const currencyList = React.useContext(GlobalContext);
  const selectedCurrency =
    (cookies.get('hk-currency') as CurrencyCodeType) ??
    ('HKD' as CurrencyCodeType);

  const selectedCurrencyDetails = currencyList?.all_currencies?.find(
    (c) => c.currency === selectedCurrency,
  );

  const [discountInfo, setDiscountInfo] = React.useState<any>(null);
  const [coupon, setCoupon] = React.useState<string>('');

  const cartID = id && id.replace('gid://shopify/Cart/', '');
  const DISCOUNTYARD_TOKEN = import.meta.env.PUBLIC_DISCOUNTYARD_TOKEN;
  const moneyObject = cost && useMoney(cost.subtotalAmount);

  const validateDiscount = async (reqObj: any) => {
    setIsLoading(true);
    const res = await fetch(
      'https://stack-discounts.merchantyard.com/api/shopify/coupons/validate',
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reqObj),
      },
    );
    const data = await res.json();
    cookies.set(
      'hk-coupons',
      data.valid_coupons && data.valid_coupons.toString(),
    );
    setDiscountInfo(data);
    setIsLoading(false);
  };

  const originalTotalPrice =
    moneyObject &&
    parseInt(moneyObject.withoutTrailingZerosAndCurrency.replace(/[,]/g, '')) *
      100;
  const hash = md5(`${originalTotalPrice}${DISCOUNTYARD_TOKEN}`);

  const reqObj = {
    additional_attributes: null,
    cart: {
      attributes: {
        language: languageCode,
      },
      cart_level_discount_applications: [],
      currency: currency,
      item_count: totalQuantity,
      items:
        lines &&
        !R.isEmpty(lines) &&
        lines.map((l) => {
          const merchandise = l.merchandise as CartMerchandise;
          const variantID = parseInt(
            l.merchandise.id.replace('gid://shopify/ProductVariant/', ''),
          );
          const product = l.merchandise.product as Pick<
            Product,
            'title' | 'handle' | 'id' | 'productType' | 'vendor'
          >;
          const price =
            parseFloat(
              new Intl.NumberFormat('zh-HK', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
                .format(parseInt(merchandise.priceV2.amount))
                .replace(/[,]/g, ''),
            ) * 100;

          const totalPrice =
            parseFloat(
              new Intl.NumberFormat('zh-HK', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
                .format(parseInt(l.cost.totalAmount.amount))
                .replace(/[,]/g, ''),
            ) * 100;

          return {
            discounted_price: price,
            discounts: [],
            featured_image: {
              alt: product.title,
              aspect_ratio: 1,
              height: 1000,
              url: l.merchandise?.image?.url,
              width: 1000,
            },
            final_line_price: totalPrice,
            final_price: price,
            gift_card: false,
            grams: 0,
            handle: product.handle,
            id: variantID,
            image: l.merchandise?.image?.url,
            //   key: '42099802767613:4ffff0b13288b36d0e7f3e2e2a781ce1',
            line_level_discount_allocations: [],
            line_level_total_discount: 0,
            line_price: totalPrice,
            options_with_values: l.merchandise.selectedOptions,
            original_line_price: totalPrice,
            original_price: price,
            price: price,
            //   product_description:
            //     '\nABUS 275 碟鎖 5MM描述：Abus 275 紅色碟鎖ABUS安全級別 7數據：重量：0.461 公斤顏色：紅色產品類型：碟鎖尺寸：5mm重量：0.461',
            product_has_only_default_variant: false,
            product_id: 7469327614205,
            product_title: product.title,
            product_type: product.productType,
            properties: {},
            quantity: l.quantity,
            requires_shipping: true,
            sku: merchandise.sku,
            taxable: false,
            title: `${product.title} - ${l.merchandise.title}`,
            total_discount: 0,
            url: `/products/${product.handle}?variant=${variantID}`,
            variant_id: variantID,
            variant_options: [l.merchandise.selectedOptions[0].value],
            variant_title: l.merchandise.title,
            vendor: product.vendor,
          };
        }),

      items_subtotal_price: originalTotalPrice,
      note: null,
      original_total_price: originalTotalPrice,
      requires_shipping: true,
      //   token: '3002f3cea1ae2f5afe4aeeb0fb5a66f0',
      //   total_discount: 0,
      total_price: originalTotalPrice,
      //   total_weight: 0,
    },
    checkout_page: 0,
    cid: '',
    coupons: [],
    d: 0,
    hash: hash,
    n: '',
    product_options: [],
    sharable_code: null,
    sharable_discounts: '',
    shopify_currency: {
      active: currency,
      rate: '1.0',
    },
    store_domain: 'helmetking-0001.myshopify.com',
  };

  React.useEffect(() => {
    if (
      lines &&
      !R.isEmpty(lines) &&
      cartID &&
      DISCOUNTYARD_TOKEN &&
      moneyObject
    ) {
      const validAndAppliedCoupons = cookies.get('hk-coupons')?.split(',');
      validateDiscount({
        ...reqObj,
        ...(validAndAppliedCoupons && {coupons: validAndAppliedCoupons}),
      });
    }
  }, [lines]);

  const submitCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      lines &&
      !R.isEmpty(lines) &&
      cartID &&
      DISCOUNTYARD_TOKEN &&
      moneyObject
    ) {
      setCoupon('');
      const validAndAppliedCoupons = await cookies
        .get('hk-coupons')
        ?.split(',');
      await validateDiscount({
        ...reqObj,
        ...(validAndAppliedCoupons && {
          coupons: validAndAppliedCoupons.concat(coupon),
        }),
      });
    }
  };

  const removeCoupon = async (couponCode: string) => {
    await validateDiscount({
      ...reqObj,
      coupons: reqObj.coupons.filter((coupon: string) => coupon !== couponCode),
    });
  };

  const appliedCoupons =
    discountInfo &&
    discountInfo?.valid_coupons &&
    discountInfo?.valid_coupons.map((c: string) => {
      return {
        name: c,
        value: discountInfo.discounts_value[c],
        automatic: discountInfo.automatic_coupons.find(
          (coupon: string) => coupon === c,
        ),
      };
    });

  return (
    <div className="mb-8">
      {!isLoading && (
        <div
          role="table"
          aria-label="Cost summary"
          className="mb-[30px] text-sm"
        >
          {discountInfo ? (
            <div className="grid gap-2">
              <div
                className="flex justify-between space-x-2 text-md"
                role="row"
              >
                <span className="font-normal" role="rowheader">
                  Subtotal:
                </span>
                <span className="text-right font-normal">
                  {/* {discountInfo?.subtotal} */}
                  {selectedCurrencyDetails?.details?.currency?.code}
                  {discountInfo?.subtotal_value?.toFixed(2)}
                </span>
              </div>
              {/* can map over discounts_value to show individual discount price, [code]: [discount_value] */}
              {discountInfo?.discount_value !== 0 && (
                <div
                  className="flex justify-between space-x-2 text-md text-hk-red"
                  role="row"
                >
                  <div className="flex items-center space-x-1">
                    <svg
                      focusable="false"
                      className="w-6"
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 640 512"
                    >
                      <path
                        fill="currentColor"
                        d="M497.941 225.941L286.059 14.059A48 48 0 0 0 252.118 0H48C21.49 0 0 21.49 0 48v204.118a48 48 0 0 0 14.059 33.941l211.882 211.882c18.744 18.745 49.136 18.746 67.882 0l204.118-204.118c18.745-18.745 18.745-49.137 0-67.882zM112 160c-26.51 0-48-21.49-48-48s21.49-48 48-48 48 21.49 48 48-21.49 48-48 48zm513.941 133.823L421.823 497.941c-18.745 18.745-49.137 18.745-67.882 0l-.36-.36L527.64 323.522c16.999-16.999 26.36-39.6 26.36-63.64s-9.362-46.641-26.36-63.64L331.397 0h48.721a48 48 0 0 1 33.941 14.059l211.882 211.882c18.745 18.745 18.745 49.137 0 67.882z"
                      ></path>
                    </svg>
                    <span className="font-normal" role="rowheader">
                      Discount Applied:
                    </span>
                  </div>
                  <span className="font-sembild text-right">
                    &#8722;
                    {/* {discountInfo?.discount} */}
                    {selectedCurrencyDetails?.details?.currency?.code}
                    {discountInfo?.discount_value?.toFixed(2)}
                  </span>
                </div>
              )}
              <div
                className="flex justify-between space-x-2 text-[22px]"
                role="row"
              >
                <span className="font-semibold" role="rowheader">
                  Total:
                </span>
                <span className="text-right font-semibold">
                  {/* {discountInfo?.total} */}
                  {selectedCurrencyDetails?.details?.currency?.code}
                  {discountInfo?.total_value?.toFixed(2)}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex space-x-2 text-[22px]" role="row">
              <span className="font-normal" role="rowheader">
                Total:
              </span>
              <CartCost
                amountType="subtotal"
                className="text-right font-normal"
                role="cell"
              />
            </div>
          )}
        </div>
      )}
      <div className="">
        <div className="mb-2 text-[20px]">Discount Code:</div>
        <form className="" onSubmit={submitCoupon}>
          <div className="relative flex w-full overflow-hidden rounded-sm border-[1px] border-black text-[22px]">
            <input
              type="text"
              value={coupon}
              className="text-medium inline-block h-full w-full py-1 px-2 outline-none"
              onChange={(e) => setCoupon(e.target.value)}
            />
            <button
              disabled={isLoading}
              className="absolute top-0 right-0 flex h-full min-w-[92px] flex-shrink-0 items-center justify-center rounded-tl-sm rounded-bl-sm bg-hk-orange p-2 text-md font-normal hover:bg-black hover:text-white"
            >
              {isLoading ? (
                <span className="block w-6">
                  <LoadingSpinner />
                </span>
              ) : (
                <span className="">Submit</span>
              )}
            </button>
          </div>
        </form>
      </div>
      <div className="mt-4 flex">
        <ul className="-mx-2 flex flex-wrap">
          {appliedCoupons &&
            appliedCoupons.map(
              (c: {name: string; value: string; automatic: boolean}) => {
                return (
                  <li className="group p-2">
                    <div className="flex items-center space-x-1 rounded-md bg-hk-gray p-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 18 18"
                        className="w-4"
                      >
                        <path
                          d="M17.78 3.09C17.45 2.443 16.778 2 16 2h-5.165c-.535 0-1.046.214-1.422.593l-6.82 6.89c0 .002 0 .003-.002.003-.245.253-.413.554-.5.874L.738 8.055c-.56-.953-.24-2.178.712-2.737L9.823.425C10.284.155 10.834.08 11.35.22l4.99 1.337c.755.203 1.293.814 1.44 1.533z"
                          fillOpacity=".55"
                        />
                        <path
                          fillOpacity=".25"
                          d="M10.835 2H16c1.105 0 2 .895 2 2v5.172c0 .53-.21 1.04-.586 1.414l-6.818 6.818c-.777.778-2.036.782-2.82.01l-5.166-5.1c-.786-.775-.794-2.04-.02-2.828.002 0 .003 0 .003-.002l6.82-6.89C9.79 2.214 10.3 2 10.835 2zM13.5 8c.828 0 1.5-.672 1.5-1.5S14.328 5 13.5 5 12 5.672 12 6.5 12.672 8 13.5 8z"
                        />
                      </svg>
                      <div className="relative">
                        <span
                          className={clsx(
                            c.automatic && 'group-hover:opacity-0',
                          )}
                        >
                          {c.name}
                        </span>
                        {c.automatic && (
                          <span
                            className={clsx(
                              c.automatic &&
                                'absolute left-0 w-full opacity-0 group-hover:opacity-100',
                            )}
                          >
                            {c.value}
                          </span>
                        )}
                        {!c.automatic && (
                          <button
                            className="ml-2 outline-none hover:opacity-50"
                            onClick={() => removeCoupon(c.name)}
                          >
                            <svg
                              focusable="false"
                              role="img"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 320 512"
                              className="w-2"
                            >
                              <path
                                fill="currentColor"
                                d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              },
            )}
        </ul>
      </div>
    </div>
  );
}

export default withCookies(CartSummaryAndDiscounts);
